import { useState } from "react"
import { Alert, Box, Collapse, Link, Stack, Typography } from "@mui/material"
import { TransitionGroup } from "react-transition-group"
import { isEmpty } from "lodash-es"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { CheckInAlertItem } from "./useCheckInAlerts"

type Props = Readonly<{
    alerts: CheckInAlertItem[]
    onRemove: (id: string) => void
}>

export function CheckInAlerts({ alerts, onRemove }: Props) {
    if (isEmpty(alerts)) {
        return null
    }

    return (
        <TransitionGroup>
            {alerts.map((alert) => (
                <Collapse key={alert.id}>
                    <Box paddingBottom="1rem">
                        <CheckInAlertRow alert={alert} onRemove={onRemove} />
                    </Box>
                </Collapse>
            ))}
        </TransitionGroup>
    )
}

type RowProps = Readonly<{
    alert: CheckInAlertItem
    onRemove: (id: string) => void
}>

function CheckInAlertRow({ alert, onRemove }: RowProps) {
    const [showHostContact, setShowHostContact] = useState(false)

    const host = alert.host
    const hostPhone = host?.mobileNumber || host?.phoneNumber

    return (
        <Alert onClose={() => onRemove(alert.id)} severity={alert.type} variant="filled">
            <Typography variant="body2">{alert.message}</Typography>
            {host && (
                <Box>
                    <LocalizedStrict id="service-center-check-in-host-contact-link">
                        <Link
                            component="button"
                            type="button"
                            color="inherit"
                            underline="always"
                            onClick={() => setShowHostContact((prev) => !prev)}
                        >
                            Show host contact details
                        </Link>
                    </LocalizedStrict>
                    {showHostContact && (
                        <Stack marginTop=".25rem" spacing=".25rem">
                            <Typography variant="body2">{host.displayName}</Typography>
                            {host.companyName && <Typography variant="body2">{host.companyName}</Typography>}
                            {host.email && (
                                <Link href={`mailto:${host.email}`} color="inherit" variant="body2">
                                    {host.email}
                                </Link>
                            )}
                            {hostPhone && (
                                <Link href={`tel:${hostPhone}`} color="inherit" variant="body2">
                                    {hostPhone}
                                </Link>
                            )}
                        </Stack>
                    )}
                </Box>
            )}
        </Alert>
    )
}
